import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useContext, useState } from "react";
import { LeadPageContext } from "@/modules/lead-page/context/lead-page.context";
import {
  previewQrCode,
  sendQrCode,
} from "@/modules/lead-page/api/lead-page.v2.api";
 import { showToast } from "@/modules/common/utils";
import { ToastType } from "@/modules/common/types";
import { QrCodePreviewResponse } from "@/modules/lead-page/new/components/header/types";

interface PreviewMutationParams {
  journeyUId: string;
  forceRegenerate: boolean;
}

interface SendMutationParams {
  journeyUId: string;
  qrCodeUrl: string;
}

const useGenerateQrCode = () => {
  const { lead } = useContext(LeadPageContext);
  const journeyUId = lead?.data?.lead?.journey_uid;
  const queryClient = useQueryClient();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [previewData, setPreviewData] = useState<QrCodePreviewResponse | null>(
    null,
  );

  const previewMutation = useMutation({
    mutationFn: ({ journeyUId, forceRegenerate }: PreviewMutationParams) =>
      previewQrCode(journeyUId, forceRegenerate),
    onSuccess: (response) => {
      setPreviewData(response.data);
      setIsModalOpen(true);
    },
    onError: () => {
      showToast({
        type: ToastType.ERROR,
        toastId: "preview-qr-code-error",
        message: "Failed to load QR preview",
      });
    },
  });

  const sendMutation = useMutation({
    mutationFn: ({ journeyUId, qrCodeUrl }: SendMutationParams) =>
      sendQrCode(journeyUId, qrCodeUrl),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lead-activity"] });
      showToast({
        type: ToastType.SUCCESS,
        toastId: "send-qr-code-success",
        message: "QR Code sent successfully",
      });
      setIsModalOpen(false);
      setPreviewData(null);
    },
    onError: () => {
      showToast({
        type: ToastType.ERROR,
        toastId: "send-qr-code-error",
        message: "Failed to send QR Code",
      });
    },
  });

  const onPreviewQrCode = () => {
    if (!journeyUId || previewMutation.isPending) return;
    previewMutation.mutate({ journeyUId, forceRegenerate: false });
  };

  const onRegenerate = () => {
    if (!journeyUId || previewMutation.isPending) return;
    previewMutation.mutate({ journeyUId, forceRegenerate: true });
  };

  const onSend = () => {
    if (!journeyUId || !previewData?.url || sendMutation.isPending) return;
    sendMutation.mutate({ journeyUId, qrCodeUrl: previewData.url });
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setPreviewData(null);
  };

  return {
    onPreviewQrCode,
    onRegenerate,
    onSend,
    isLoading: previewMutation.isPending && !isModalOpen,
    isModalOpen,
    previewData,
    isRegenerating: previewMutation.isPending && isModalOpen,
    isSending: sendMutation.isPending,
    closeModal,
  };
};

export default useGenerateQrCode;
